import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useUser } from '../context/UserContext';
import './ContractReviewModal.css';

export default function ContractReviewModal({ tenderId, tenderName, onClose, onComplete }) {
  const { user } = useUser();

  const [review, setReview] = useState({
    order_aligned: null,
    order_aligned_comments: '',
    contract_sum_agreed: null,
    contract_sum_agreed_comments: '',
    contractual_framework_reviewed: null,
    contractual_framework_reviewed_comments: '',
    precursory_actions_complete: null,
    precursory_actions_comments: '',
    final_confirmed: false,
    final_confirmed_at: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!tenderId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await api.getContractReview(tenderId);
        if (cancelled) return;
        const payload = result?.data || result;
        if (payload && typeof payload === 'object') {
          setReview(prev => ({ ...prev, ...payload }));
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load contract review');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenderId]);

  const canEdit = ['manager', 'director'].includes(user?.role);
  const canConfirm = user?.role === 'director';

  const handleCheckboxChange = (field, value) => {
    setReview(prev => ({ ...prev, [field]: value }));
  };

  const handleCommentChange = (field, value) => {
    setReview(prev => ({ ...prev, [field]: value }));
  };

  const allAnswered = [
    review.order_aligned,
    review.contract_sum_agreed,
    review.contractual_framework_reviewed,
    review.precursory_actions_complete,
  ].every(val => val !== null && val !== undefined);

  const handleConfirm = async () => {
    if (!allAnswered) {
      setError('All 4 items must be answered');
      return;
    }
    // Backend requires comments when precursory_actions_complete === false
    if (review.precursory_actions_complete === false && !review.precursory_actions_comments?.trim()) {
      setError('Comments are mandatory when precursory actions are not complete');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const payload = {
        order_aligned: review.order_aligned,
        order_aligned_comments: review.order_aligned_comments,
        contract_sum_agreed: review.contract_sum_agreed,
        contract_sum_agreed_comments: review.contract_sum_agreed_comments,
        contractual_framework_reviewed: review.contractual_framework_reviewed,
        contractual_framework_reviewed_comments: review.contractual_framework_reviewed_comments,
        precursory_actions_complete: review.precursory_actions_complete,
        precursory_actions_comments: review.precursory_actions_comments,
        final_confirmed: true,
      };
      const result = await api.saveContractReview(tenderId, payload);
      const respPayload = result?.data || result;
      if (respPayload && typeof respPayload === 'object') {
        setReview(prev => ({ ...prev, ...respPayload }));
      }
      onComplete?.(respPayload);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to confirm review');
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { key: 'order_aligned',                  label: 'Order Aligned' },
    { key: 'contract_sum_agreed',            label: 'Contract Sum Agreed' },
    { key: 'contractual_framework_reviewed', label: 'Contractual Framework Reviewed' },
    { key: 'precursory_actions_complete',    label: 'Precursory Actions Complete' },
  ];

  return (
    <div className="contract-review-modal-overlay">
      <div className="contract-review-modal">
        <div className="modal-header">
          <div>
            <h2>Contract Review Checklist</h2>
            {tenderName && <p className="tender-ref">{tenderName}</p>}
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {loading && !review.final_confirmed && <div className="loading-message">Loading…</div>}

          {fields.map(({ key, label }) => {
            const commentKey = key + '_comments';
            return (
              <div key={key} className="checklist-item">
                <label>
                  <input
                    type="checkbox"
                    checked={review[key] === true}
                    onChange={(e) => handleCheckboxChange(key, e.target.checked)}
                    disabled={!canEdit || review.final_confirmed}
                  />
                  <span>{label}</span>
                </label>
                {review[key] === false && (
                  <textarea
                    placeholder="Comment required"
                    value={review[commentKey] || ''}
                    onChange={(e) => handleCommentChange(commentKey, e.target.value)}
                    disabled={!canEdit || review.final_confirmed}
                    className="required"
                  />
                )}
                {canEdit && review[key] !== false && (
                  <textarea
                    placeholder="Add comment (optional)"
                    value={review[commentKey] || ''}
                    onChange={(e) => handleCommentChange(commentKey, e.target.value)}
                    disabled={review.final_confirmed}
                  />
                )}
              </div>
            );
          })}

          {review.final_confirmed && review.final_confirmed_at && (
            <div className="confirmed-banner">
              ✓ Confirmed at {new Date(review.final_confirmed_at).toLocaleString()}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          {!review.final_confirmed && canConfirm && (
            <button
              className="btn-confirm"
              onClick={handleConfirm}
              disabled={!allAnswered || loading}
            >
              {loading ? 'Confirming…' : 'Confirm Review'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
